import React, { useMemo } from 'react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Rendered before each Monday in BOTH the bar row and the label row — identical
// box metrics keep the two flex rows column-aligned.
const WEEK_SEPARATOR_STYLE = { width: 1, margin: '0 1px', flexShrink: 0 };

const LegendItem = ({ color, label }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
    <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block' }} />
    {label}
  </span>
);

const EarningsHistogram = ({ histogram, onBarClick, selectedDate }) => {
  // Build full 30-day array, filling gaps with zero
  const bars = useMemo(() => {
    if (!histogram?.length) return [];

    const countMap = {};
    const bellwetherMap = {};
    for (const h of histogram) {
      countMap[h.date] = h.count;
      if (h.bellwethers?.length) bellwetherMap[h.date] = h.bellwethers;
    }

    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const dayOfWeek = d.getDay();
      // Weekends hidden: real weekend reporters are extremely rare (BRK-B Saturdays),
      // and weekend dates in the data are almost always stale estimates.
      if (dayOfWeek === 0 || dayOfWeek === 6) continue;
      const iso = d.toISOString().split('T')[0];
      result.push({
        date: iso,
        count: countMap[iso] || 0,
        bellwethers: bellwetherMap[iso] || [],
        dayLabel: DAYS[dayOfWeek],
        dateLabel: `${d.getDate()}`,
        monthLabel: MONTHS[d.getMonth()],
        isWeekStart: dayOfWeek === 1 && result.length > 0,
        isThisWeek: i < 7,
        isToday: i === 0,
      });
    }
    return result;
  }, [histogram]);

  const maxCount = useMemo(() => {
    return Math.max(...bars.map(b => b.count), 1);
  }, [bars]);

  if (!bars.length) return null;

  // On phones 30 labeled columns can't fit; only label the days that matter.
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div style={{ padding: '0 16px', marginBottom: 8 }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: '12px 12px 6px',
        border: '1px solid #f0f0f0',
      }}>
        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
            Earnings Next 30 Days
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LegendItem color="#f59e0b" label="Bellwethers" />
            <LegendItem color="#6366f1" label="This week" />
            <LegendItem color="#c7d2fe" label="Later" />
          </div>
        </div>

        {/* Bars */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 64 }}>
          {bars.map((bar) => {
            const heightPct = bar.count > 0 ? Math.max((bar.count / maxCount) * 100, 6) : 0;
            const isActiveWeek = bar.isThisWeek;
            const hasBellwethers = bar.bellwethers.length > 0;
            const barColor = hasBellwethers
              ? (isActiveWeek ? '#f59e0b' : '#fbbf24')
              : (isActiveWeek ? '#6366f1' : '#c7d2fe');
            const bellwetherTip = hasBellwethers
              ? ` — ${bar.bellwethers.slice(0, 5).join(', ')}${bar.bellwethers.length > 5 ? ` +${bar.bellwethers.length - 5} more` : ''}`
              : '';

            const showLabel = bar.count > 0 && (!isNarrow || hasBellwethers || bar.count === maxCount);
            const isClickable = !!onBarClick && bar.count > 0;
            const isSelected = selectedDate === bar.date;

            return (
              <React.Fragment key={bar.date}>
                {bar.isWeekStart && <div style={{ ...WEEK_SEPARATOR_STYLE, alignSelf: 'stretch', background: '#e5e7eb' }} />}
              <div
                style={{
                  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'flex-end', height: '100%', position: 'relative',
                  cursor: isClickable ? 'pointer' : 'default',
                  background: isSelected ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                  borderRadius: 3,
                }}
                title={`${bar.dayLabel} ${bar.monthLabel} ${bar.dateLabel}: ${bar.count} stocks${bellwetherTip}`}
                onClick={isClickable ? () => onBarClick(bar.date) : undefined}
                role={isClickable ? 'button' : undefined}
              >
                {showLabel && (
                  <span style={{
                    // Absolutely positioned so wide labels don't set a min-width on the
                    // column — 30 labeled columns would overflow a phone viewport.
                    position: 'absolute', bottom: `calc(${heightPct}% + 2px)`,
                    left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap',
                    fontSize: 8, fontWeight: hasBellwethers ? 700 : 600,
                    color: hasBellwethers ? '#d97706' : '#9ca3af',
                    fontFamily: 'ui-monospace, monospace',
                  }}>
                    {bar.count}
                  </span>
                )}
                <div style={{
                  width: '100%', maxWidth: 24,
                  height: bar.count > 0 ? `${heightPct}%` : 0,
                  background: barColor,
                  borderRadius: '3px 3px 0 0',
                  transition: 'height 0.3s ease',
                  minHeight: bar.count > 0 ? 3 : 0,
                  boxShadow: isSelected
                    ? '0 0 0 1.5px #4f46e5'
                    : (hasBellwethers ? '0 0 0 1px rgba(217, 119, 6, 0.25)' : 'none'),
                }} />
              </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Date labels */}
        <div style={{ display: 'flex', gap: 1, marginTop: 2 }}>
          {bars.map((bar) => (
            <React.Fragment key={bar.date}>
              {bar.isWeekStart && <div style={WEEK_SEPARATOR_STYLE} />}
            <div style={{ flex: 1, textAlign: 'center', overflow: 'hidden' }}>
              <div style={{
                fontSize: 8, color: bar.isToday ? '#6366f1' : '#9ca3af',
                fontWeight: bar.isToday ? 700 : 400,
                lineHeight: 1.3,
              }}>
                {bar.dayLabel.charAt(0)}
              </div>
              <div style={{
                fontSize: 7, color: bar.isToday ? '#6366f1' : '#d1d5db',
                fontWeight: bar.isToday ? 700 : 400,
                lineHeight: 1.3,
              }}>
                {bar.dateLabel}
              </div>
            </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(EarningsHistogram);
